import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { loginRequest, logoutRequest, pollTokenRequest } from '../services/base/authService';
import { envVarService } from '../services/base/envVarService';
import { localStorageService } from '../services/base/localStorageService';
import { urlParameterService } from '../services/base/urlParameterService';

const AuthContext = createContext();

/**
 * Resolve the effective user id for all backend calls.
 *
 * A `?userId=` on the URL (set by the NiceCxOne iframe at launch) replaces the AD
 * identity. Doing it here, at the identity source, means every existing caller
 * that already sends `user.userId` as a query parameter -- getChat,
 * getChatHistory, updateChat, sendMessage, fileUpload, sendFeedback -- keys
 * correctly with no change of its own. It also guarantees the write key matches
 * the key Clara's /pubsub-webhook writes under, which is what makes pushed
 * messages render in the right conversation.
 *
 * Threading an "effectiveUserId" through each call site instead is how you end up
 * writing under one key and reading under another, which fails silently.
 *
 * Only overrides when the parameter is actually present, so every other agent
 * behaves exactly as before.
 */
const resolveEffectiveUserId = (adUserId) => {
  try {
    const urlUserId = urlParameterService.getUserIdFromUrl();
    if (urlUserId) {
      console.log('[AuthContext] Using userId from URL (NiceCxOne):', urlUserId);
      return urlUserId;
    }
  } catch (error) {
    console.warn('[AuthContext] Failed to read userId from URL:', error);
  }
  return adUserId;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [bypassLogin, setBypassLogin] = useState(null);

  // Fetch the bypass login variable once the component mounts
  useEffect(() => {
    const fetchBypassLogin = async () => {
      const value = await envVarService.getEnvVar('REACT_APP_BYPASS_LOGIN');
      setBypassLogin(value);
    };
    fetchBypassLogin();
  }, []);

  // Wrap validatelogin in useCallback to prevent recreating on every render
  const validatelogin = useCallback((token) => {
    if (!token) {
      throw new Error("Token is required");
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error("Invalid JWT token");
    }

    try {
      const payload = parts[1];
      const decodedPayload = JSON.parse(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      );

      // Extract user information from ID token claims
      const idTokenClaims = decodedPayload.id_token_claims || {};

      // Prioritize ID token claims over decodedPayload for consistency
      const userName = idTokenClaims.name || idTokenClaims.displayName || decodedPayload.displayName || decodedPayload.name || '';
      const givenName = idTokenClaims.given_name || idTokenClaims.givenName || decodedPayload.givenName || '';
      const surname = idTokenClaims.family_name || idTokenClaims.surname || decodedPayload.surname || '';
      const userEmail = decodedPayload.userPrincipalName || idTokenClaims.preferred_username || idTokenClaims.upn || '';

      console.log('Extracted fields:', { userName, givenName, surname, userEmail });

      const adUserId = decodedPayload.userPrincipalName;

      const userData = {
        signedIn: true,
        userId: resolveEffectiveUserId(adUserId),
        userName: userName,
        userEmail: userEmail,
        givenName: givenName,
        surname: surname,
        token,
        userGroups: idTokenClaims.groups || []
      };
      console.log(userData);
      setUser(userData);

      // Save to localStorage with 24hr expiry.
      // Persist the AD identity, NOT the effective id: a NiceCxOne userId belongs
      // to one iframe launch, and persisting it would leak that id into a later,
      // unrelated session in the same browser. The override is re-derived from the
      // URL on every mount instead.
      localStorageService.saveAuthData(userData.token, adUserId, userData.userGroups, userData.userName, userData.userEmail, userData.givenName, userData.surname);
    } catch (error) {
      throw new Error("Failed to decode token: " + error.message);
    }
  }, []);

  // Monitor localStorage changes (for debugging)
  useEffect(() => {
    const handleStorageChange = (e) => {
      console.log('[AuthContext] Storage event detected!');
      console.log('[AuthContext] - Key:', e.key);
      console.log('[AuthContext] - Old value:', e.oldValue);
      console.log('[AuthContext] - New value:', e.newValue);
      console.log('[AuthContext] - URL:', e.url);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Restore user session from localStorage or URL token on mount
  useEffect(() => {
    console.log('[AuthContext] Restore session useEffect triggered');
    console.log('[AuthContext] Current user state:', user);

    const restoreSession = () => {
      console.log('[AuthContext] restoreSession() called');

      // DEBUG: Check what's actually in localStorage
      console.log('[AuthContext] DEBUG - Storage Diagnostic:');
      console.log('[AuthContext] - window.location.href:', window.location.href);
      console.log('[AuthContext] - window.location.origin:', window.location.origin);
      console.log('[AuthContext] - document.domain:', document.domain);
      console.log('[AuthContext] - localStorage available?:', typeof localStorage !== 'undefined');
      console.log('[AuthContext] - Total localStorage keys:', localStorage.length);

      console.log('[AuthContext] DEBUG - All localStorage keys:');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        console.log(`[AuthContext] - Key ${i}: ${key}`);
        if (key && key.startsWith('nrg_chatbot')) {
          const value = localStorage.getItem(key);
          console.log(`[AuthContext]   Value length: ${value ? value.length : 0} chars`);
          console.log(`[AuthContext]   Value:`, value);
        }
      }

      // Try to manually check each expected key
      console.log('[AuthContext] DEBUG - Direct key checks:');
      console.log('[AuthContext] - nrg_chatbot_user_data exists?:', localStorage.getItem('nrg_chatbot_user_data') !== null);
      console.log('[AuthContext] - nrg_chatbot_session_data exists?:', localStorage.getItem('nrg_chatbot_session_data') !== null);
      console.log('[AuthContext] - nrg_chatbot_last_bot_id exists?:', localStorage.getItem('nrg_chatbot_last_bot_id') !== null);

      // Check for token in URL first (non-iframe redirect flow)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');

      if (tokenFromUrl) {
        console.log('[AuthContext] Token found in URL, validating...');
        try {
          validatelogin(tokenFromUrl);
          // Remove token from URL after processing using urlParameterService
          // This ensures token is removed securely and consistently
          urlParameterService.updateUrlParameters(undefined, undefined, true); // true = remove token
        } catch (error) {
          console.error('[AuthContext] Failed to validate token from URL:', error);
        }
        return; // Don't check localStorage if we processed URL token
      }

      console.log('[AuthContext] No token in URL, checking localStorage...');

      // Otherwise, try to restore from localStorage
      const storedAuth = localStorageService.getAuthData();

      if (storedAuth) {
        const userData = {
          signedIn: true,
          // storedAuth.userId is the AD identity (see saveAuthData above), so the
          // URL override has to be re-applied on every restore.
          userId: resolveEffectiveUserId(storedAuth.userId),
          token: storedAuth.token,
          userGroups: storedAuth.userGroups,
          userName: storedAuth.userName,
          userEmail: storedAuth.userEmail,
          givenName: storedAuth.givenName,
          surname: storedAuth.surname,
        };
        console.log('[AuthContext] ? Session restored from localStorage:', userData);
        setUser(userData);
      } else {
        console.log('[AuthContext] ? No valid auth data in localStorage');
      }
    };

    // Only restore if user is not already set
    if (!user) {
      console.log('[AuthContext] User is null, attempting to restore session...');
      restoreSession();
    } else {
      console.log('[AuthContext] User already set, skipping restore');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatelogin]); // Run when validatelogin is available

  const login = () => {
    if (bypassLogin) {
      const adUserId = '12345';
      const userData = {
        signedIn: true,
        userId: resolveEffectiveUserId(adUserId),
        userName: 'Test User',
        userEmail: 'test.user@example.com',
        givenName: 'Test',
        surname: 'User',
        token: 'valid_token',
        userGroups: ['*']
      };
      console.log(userData);
      setUser(userData);
      // Save to localStorage with 24hr expiry (persist the base id, not the override)
      localStorageService.saveAuthData(userData.token, adUserId, userData.userGroups, userData.userName, userData.userEmail, userData.givenName, userData.surname);
    } else {
      // Detect if running in an iframe
      const isInIframe = window.self !== window.top;
      console.log('[AuthContext] Running in iframe:', isInIframe);

      loginRequest(isInIframe) // Pass true if in iframe
        .then((response) => {
          console.log('[AuthContext] auth_url received');

          if (isInIframe) {
            // Open popup window for authentication (iframe-compatible)
            const width = 500;
            const height = 600;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;

            const popup = window.open(
              response.data.auth_url,
              'msalAuth',
              `width=${width},height=${height},left=${left},top=${top},toolbar=0,location=0,menubar=0`
            );

            if (!popup || popup.closed || typeof popup.closed === 'undefined') {
              console.error('[AuthContext] Popup blocked!');
              alert('Popup blocked! Please allow popups for this site to authenticate.');
              return;
            }

            // Poll the backend for the token. The popup auth flow stores the JWT
            // server-side keyed by nonce. This avoids browser storage partitioning
            // and COOP issues that break postMessage/BroadcastChannel/localStorage.
            const nonce = response.data.auth_nonce;
            console.log('[AuthContext] Starting backend token polling');
            const pollInterval = setInterval(async () => {
              try {
                const result = await pollTokenRequest(nonce);
                if (result.status === 200 && result.data && result.data.jwt_token) {
                  clearInterval(pollInterval);
                  console.log('[AuthContext] Token received via backend polling');
                  validatelogin(result.data.jwt_token);
                  if (popup && !popup.closed) popup.close();
                }
              } catch (e) {
                // 204 = not ready yet, keep polling. Network errors also just retry.
              }
            }, 2000);
            // Stop polling after 2 minutes
            setTimeout(() => {
              clearInterval(pollInterval);
              console.log('[AuthContext] Token polling timed out');
            }, 120000);
          } else {
            // Traditional full-page redirect for non-iframe scenarios
            window.location.href = response.data.auth_url;
          }
        })
        .catch((error) => {
          const userData = { signedIn: false, userId: '' };
          console.log(userData);
          setUser(userData);
          console.error(error);
        });
    }
  };

  const logout = () => {
    // Clear localStorage on logout
    localStorageService.clearAll();

    logoutRequest()
        .then((response) => {
          console.log("logout_url: " + response.data.logout_url);
          setUser(null);
          console.log('Signed out');
          window.location.href = response.data.logout_url;
        })
        .catch((error) => {
          console.log("Error Logging out");
          setUser(null);
          console.error(error);
        });
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, validatelogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
